<template>
  <div>
    <div class="d-none d-md-flex">
      <div ref="cancelBtnRef" class="cancel-session-anchor" @keydown.esc="closeMenu">
        <button
          v-if="cancelSessionStatus"
          ref="menuBtn"
          type="button"
          class="session-menu-btn"
          aria-haspopup="menu"
          :aria-expanded="openCancelBtn ? 'true' : 'false'"
          aria-label="Session options"
          @click.stop="openCancelBtn = !openCancelBtn"
        >
          <!-- Decorative: the button's aria-label already names the control. -->
          <img alt="" class="session-menu-icon" src="../assets/icons/dots.svg" />
        </button>
        <button
          v-if="openCancelBtn"
          type="button"
          class="cancel-session"
          :disabled="hasReservations"
          :title="hasReservations ? cancelBlockedMessage : null"
          @click.stop="CancelSession(session)"
        >
          Cancel show
          <span v-if="hasReservations" class="cancel-session-reason">
            {{ cancelBlockedMessage }}
          </span>
        </button>
      </div>
    </div>
    <div class="d-flex d-md-none flex-column">
      <button
        v-if="cancelSessionStatus"
        class="cancel-session-mobile fontRoboto font-weight-bold font-14 justify-content-center pt-18 pb-18"
        :disabled="hasReservations"
        @click="CancelSession(session)"
      >
        Cancel session
      </button>
      <p v-if="cancelSessionStatus && hasReservations" class="cancel-session-reason px-3 pt-2">
        {{ cancelBlockedMessage }}
      </p>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useClickOutside } from '@/composables/useClickOutside';
import { Modal } from 'bootstrap';
import { API } from '@/utils/amplifyCompat';
export default {
  setup() {
    const cancelBtnRef = ref(null);
    const openCancelBtn = ref(false);

    const hide = () => {
      openCancelBtn.value = false;
    };

    useClickOutside(cancelBtnRef, hide);

    return {
      cancelBtnRef,
      openCancelBtn,
    };
  },
  props: {
    session: Object,
  },
  data() {
    return {
      cancelSessionStatus: true,
      reservationCount: 0,
    };
  },
  computed: {
    // A show with student reservations cannot be cancelled (DSL-708). cancelModals
    // re-checks on confirm, so a failed lookup here only loses the early hint.
    hasReservations() {
      return this.reservationCount > 0;
    },
    cancelBlockedMessage() {
      const n = this.reservationCount;
      return `Has ${n} reservation${n === 1 ? '' : 's'}. Cancel them first.`;
    },
  },
  async mounted() {
    try {
      const orders =
        (await API.get('asulink', `/scheduler/session/${this.session.session_id}/orders`)) || [];
      this.reservationCount = orders.length;
      if (orders.some((order) => order.checked_in)) {
        this.cancelSessionStatus = false;
      }
    } catch (err) {
      console.error('Could not load reservations for session', err);
    }
  },
  methods: {
    // Escape closes the menu and hands focus back to the toggle, so keyboard users
    // keep their place in the card instead of being dropped on <body>.
    closeMenu() {
      this.openCancelBtn = false;
      this.$refs.menuBtn?.focus();
    },
    CancelSession(session) {
      if (this.hasReservations) return;
      this.$store.state.cancelSession = session;
      this.$store.state.CancelReservation = null;
      this.$nextTick(() => {
        const modalEl = document.getElementById('cancel-modal');
        if (modalEl) {
          const modal = Modal.getOrCreateInstance(modalEl);
          modal.show();
        }
      });
    },
  },
};
</script>

<style>
/* Anchors the popup to the dots icon. Without this the popup was absolutely
   positioned against whatever distant ancestor happened to be positioned, so it
   landed nowhere near the button that opened it. */
.cancel-session-anchor {
  position: relative;
  display: inline-flex;
  align-items: center;
}
/* No background — just the icon. The 30px box is kept as the click target so it
   still lines up with the chevron beside it. */
.session-menu-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
}
.session-menu-btn:hover .session-menu-icon {
  opacity: 0.7;
}
.session-menu-btn:focus-visible,
.cancel-session:focus-visible {
  outline: 2px solid #ffc627;
  outline-offset: 2px;
}
/* dots.svg is a horizontal ellipsis; rotate it into the vertical kebab. */
.session-menu-icon {
  width: 15px;
  transform: rotate(90deg);
}

.cancel-session {
  position: absolute;
  /* Drops below the dots, right edges aligned so it never overflows the card. */
  top: calc(100% + 8px);
  right: 0;
  white-space: nowrap;
  padding: 14px 22px;
  border: none !important;
  background: #2c2c2c;
  color: #f2645a;
  font-size: 15px;
  z-index: 999;
  border-radius: 10px;
  box-shadow: 0 4px 14px rgb(0 0 0 / 50%);
}
.cancel-session:hover {
  background: #343434;
  color: #ff8377;
}
.cancel-session:disabled {
  cursor: not-allowed;
  color: #8a8a8a;
  background: #2c2c2c;
}
.cancel-session-mobile {
  cursor: pointer;
  color: #ffc627 !important;
  border: none !important;
  border-radius: 0px !important;
}
.cancel-session-mobile:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
.cancel-session-reason {
  display: block;
  margin: 4px 0 0;
  font-size: 12px;
  color: #d0d0d0;
}
</style>
